import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { PiCheckCircleLight, PiShieldWarningLight } from 'react-icons/pi';
import GlassCard from '../../shared/brand/GlassCard';
import { Well } from '../../shared/brand/Field';
import FormSection from '../../shared/FormComponents/FormSection';
import FormActions from '../../shared/FormComponents/FormActions';
import Button from '../../shared/TailwindComponents/Button';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import type { SkeletonVariant } from '../../shared/TailwindComponents/Skeleton';
import type { SettingsRequirement, SettingsViewer } from './settingsRegistry';

/**
 * The chrome every settings section wears.
 *
 * A section body decides WHAT it edits; this decides how it announces itself
 * (title/description/icon), how it says it is loading, and how it commits —
 * the three things that were being re-invented per tab.
 *
 * Existing sections do not have to adopt it yet: phase 1 renders them
 * unchanged inside `SettingsLayout`. New and restyled sections should compose
 * this so the save affordance is the same everywhere.
 *
 * Save modes:
 *   explicit - a Save button, enabled only while `dirty`. The default.
 *   instant  - no button; each control writes on change, and the header shows a
 *              transient "Saved" once `dirty` goes false after having been true.
 *   batch    - Save + Discard, with a count of pending edits ("3 unsaved changes").
 */

export type SettingsSaveMode = 'explicit' | 'instant' | 'batch';

export interface SettingsSectionProps {
  /** Already-translated section title. */
  title: React.ReactNode;
  /** Already-translated muted line under the title. */
  description?: string;
  icon?: React.ReactNode;
  /** How this section commits. Default 'explicit'. */
  saveMode?: SettingsSaveMode;
  /** Swap the body for a skeleton. */
  isLoading?: boolean;
  /** Shape of that skeleton. Default 'form'. */
  loadingVariant?: SkeletonVariant;
  /** There are unsaved edits. Drives the footer and the "Saved" affordance. */
  dirty?: boolean;
  /** Number of pending edits, shown by `saveMode="batch"`. */
  pendingCount?: number;
  isSaving?: boolean;
  onSave?: () => void;
  onDiscard?: () => void;
  saveLabel?: string;
  /** Left-hand slot of the footer row (a destructive action, a hint). */
  footerLeading?: React.ReactNode;
  /** Extra buttons/links in the header row. */
  actions?: React.ReactNode;
  /**
   * Same shape the registry uses. When given together with `viewer` and not
   * met, the section renders its not-authorized state instead of the body.
   */
  requires?: SettingsRequirement;
  viewer?: SettingsViewer;
  /** Renders the body as-is instead of in FormSection's field grid. Default true. */
  noGrid?: boolean;
  /**
   * OPT-IN: this section's body is a LIST, so it fills the settings pane
   * instead of being sized by its content - the table grows with the screen
   * and its pager sits at the bottom of the pane rather than mid-canvas.
   *
   * Carries `fill` down through `FormSection` to `GlassCard`, which is the
   * whole point: a `h-full` list inside a content-sized card resolves against
   * `auto` and grows to all of its rows with nothing scrolling. The body is
   * expected to own its scroller - `LIST_SECTION_SHELL` is the shape for it.
   * Field forms leave this off.
   */
  fill?: boolean;
  /** Field-grid columns when `noGrid` is false. */
  gridCols?: 1 | 2 | 3 | 4;
  className?: string;
  children: React.ReactNode;
}

const SAVED_VISIBLE_MS = 2000;

/** Local mirror of the registry check, so a section can gate itself standalone. */
const meetsRequirement = (requires: SettingsRequirement, viewer: SettingsViewer): boolean => {
  if (requires.flag && !requires.flag()) return false;
  if (requires.orgAdmin && !viewer.isOrgAdmin) return false;
  if (requires.permission && viewer.accessRights?.[requires.permission] === false) return false;
  return true;
};

export const SettingsNotAuthorized: React.FC<{ message?: string }> = ({ message }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <PiShieldWarningLight className="w-12 h-12 text-fg-muted mb-4" />
      <h3 className="text-lg font-medium text-fg-strong mb-2">{t('Admin Access Required')}</h3>
      <p className="text-sm text-fg-muted max-w-md">
        {message ||
          t('Only organization administrators can manage these settings. Contact your admin if you need changes.')}
      </p>
    </div>
  );
};

const SettingsSection: React.FC<SettingsSectionProps> = ({
  title,
  description,
  icon,
  saveMode = 'explicit',
  isLoading = false,
  loadingVariant = 'form',
  dirty = false,
  pendingCount,
  isSaving = false,
  onSave,
  onDiscard,
  saveLabel,
  footerLeading,
  actions,
  requires,
  viewer,
  noGrid = true,
  gridCols = 1,
  fill = false,
  className,
  children,
}) => {
  const { t } = useTranslation();

  // "Saved" appears when a dirty section stops being dirty - which is what an
  // instant-save control does on every successful write.
  const [showSaved, setShowSaved] = useState(false);
  const wasDirty = useRef(dirty);
  useEffect(() => {
    const justSaved = wasDirty.current && !dirty;
    wasDirty.current = dirty;
    if (!justSaved) return undefined;
    setShowSaved(true);
    const timer = setTimeout(() => setShowSaved(false), SAVED_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [dirty]);

  if (requires && viewer && !meetsRequirement(requires, viewer)) {
    return (
      <GlassCard className={className}>
        <SettingsNotAuthorized />
      </GlassCard>
    );
  }

  const savedBadge = showSaved ? (
    <span className="flex items-center gap-1 text-xs text-success-600 dark:text-success-400">
      <PiCheckCircleLight className="w-4 h-4" />
      {t('Saved')}
    </span>
  ) : null;

  const footer = () => {
    if (isLoading) return null;
    if (saveMode === 'instant') {
      return (
        <p className="pt-4 text-xs text-fg-muted">{t('Changes are saved as you make them.')}</p>
      );
    }
    if (!onSave) return null;
    if (saveMode === 'batch') {
      return (
        <FormActions
          divider
          leading={
            footerLeading || (
              <span className="text-xs text-fg-muted">
                {dirty
                  ? t('{{count}} unsaved change(s)', { count: pendingCount ?? 0 })
                  : t('No unsaved changes')}
              </span>
            )
          }
        >
          {onDiscard && (
            <Button variant="outline" size="small" onClick={onDiscard} disabled={!dirty || isSaving}>
              {t('Discard')}
            </Button>
          )}
          <Button
            variant="primary"
            size="small"
            onClick={onSave}
            disabled={!dirty || isSaving}
            isLoading={isSaving}
          >
            {saveLabel || t('Save changes')}
          </Button>
        </FormActions>
      );
    }
    return (
      <FormActions divider leading={footerLeading}>
        <Button
          variant="primary"
          size="small"
          onClick={onSave}
          disabled={!dirty || isSaving}
          isLoading={isSaving}
        >
          {saveLabel || t('Save')}
        </Button>
      </FormActions>
    );
  };

  return (
    <div
      className={clsx(fill ? 'flex h-full min-h-0 flex-col' : 'space-y-4', className)}
      data-settings-save-mode={saveMode}
    >
      <FormSection
        title={title}
        description={description}
        icon={icon}
        noGrid={noGrid}
        gridCols={gridCols}
        fill={fill}
        actions={
          (savedBadge || actions) && (
            <div className="flex items-center gap-3">
              {savedBadge}
              {actions}
            </div>
          )
        }
      >
        {isLoading ? <Skeleton variant={loadingVariant} /> : children}
        {footer()}
      </FormSection>
    </div>
  );
};

/** A sunken sub-block inside a section - the standard inner well. */
export const SettingsSubsection: React.FC<{
  title?: React.ReactNode;
  description?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ title, description, className, children }) => (
  <Well className={clsx('space-y-3', className)}>
    {(title || description) && (
      <div className="space-y-1">
        {title && <h4 className="text-sm font-medium text-fg-strong">{title}</h4>}
        {description && <p className="text-xs text-fg-muted">{description}</p>}
      </div>
    )}
    {children}
  </Well>
);

export default SettingsSection;
